<template>
  <div class="tools-kb-page">
    <div v-if="paramError" class="tools-kb-error-wrap">
      <div class="tools-kb-error-card">
        <div class="tools-kb-error-icon">📚</div>
        <p class="tools-kb-error">{{ paramError }}</p>
        <p class="tools-kb-error-hint">请通过带 uid 参数的链接访问，例如 /tools/knowledge-base?uid=xxx 或 /tools/knowledge-base?uid=xxx&dir=目录id</p>
      </div>
    </div>

    <AppMask
      v-else-if="!isReady"
      title="知识库"
      icon-right="md-folder-open"
      :loading="true"
    >
      <div class="tools-kb-login-hint">正在为你准备知识空间，请稍候…</div>
    </AppMask>

    <div v-else class="tools-kb-shell">
      <div class="tools-kb-body">
        <KnowledgeLibrary
          class="tools-kb-library"
          :library-view-mode.sync="libraryViewMode"
        />
      </div>
    </div>
  </div>
</template>

<script>
import AppMask from '@/components/app/mask-loading';
import KnowledgeLibrary from '@/pages/core/knowledge/library.vue';

export default {
  name: 'ToolsKnowledgeBase',
  metaInfo: {
    title: '知识库',
  },
  components: {
    AppMask,
    KnowledgeLibrary,
  },
  data() {
    return {
      isReady: false,
      libraryViewMode: 'all',
    };
  },
  computed: {
    uid() {
      const uid = this.$route.query && this.$route.query.uid;
      return uid != null && String(uid).trim() !== '' ? String(uid).trim() : '';
    },
    paramError() {
      if (!this.uid) {
        return '缺少登录参数 uid，无法进入知识库';
      }
      return '';
    },
  },
  watch: {
    uid: {
      handler(uid) {
        if (uid) {
          this.login(uid);
        } else {
          this.isReady = false;
        }
      },
      immediate: true,
    },
  },
  methods: {
    login(uid) {
      this.isReady = false;
      this.$store.dispatch('session/login_zzl', { uid })
        .catch(() => {
          this.$Message.error('登录失败');
        })
        .finally(() => {
          this.isReady = true;
        });
    },
  },
};
</script>

<style lang="less" scoped>
.tools-kb-page {
  --kb-accent: #1aa6a0;
  --kb-accent-soft: rgba(26, 166, 160, 0.12);
  --kb-accent-strong: #0e8f8a;
  --kb-warm: #ff8a5b;
  --kb-panel: #ffffff;
  --kb-ink: #1f2a37;
  --kb-muted: #6b7a8a;
  --kb-line: rgba(31, 42, 55, 0.08);
  --kb-shadow: 0 10px 28px rgba(31, 62, 72, 0.08);

  height: 100%;
  min-height: 100%;
  min-height: 100dvh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background:
    radial-gradient(circle at 12% 8%, rgba(26, 166, 160, 0.16), transparent 42%),
    radial-gradient(circle at 88% 0%, rgba(255, 138, 91, 0.12), transparent 36%),
    linear-gradient(180deg, #f4fbfa 0%, #eef5f7 48%, #f7f8fa 100%);
  -webkit-tap-highlight-color: transparent;
}

.tools-kb-error-wrap,
.tools-kb-login-hint {
  color: var(--kb-muted);
}

.tools-kb-login-hint {
  margin-top: 8px;
  font-size: 14px;
}

.tools-kb-error-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.tools-kb-error-card {
  max-width: 420px;
  text-align: center;
  padding: 36px 28px;
  border-radius: 20px;
  background: var(--kb-panel);
  box-shadow: var(--kb-shadow);
  border: 1px solid var(--kb-line);
}

.tools-kb-error-icon {
  font-size: 42px;
  line-height: 1;
  margin-bottom: 12px;
}

.tools-kb-error {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--kb-ink);
}

.tools-kb-error-hint {
  margin: 10px 0 0;
  font-size: 13px;
  color: var(--kb-muted);
  line-height: 1.5;
}

.tools-kb-shell {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 10px 12px 12px;
}

.tools-kb-body {
  flex: 1;
  min-height: 0;
  border-radius: 18px;
  overflow: hidden;
  background: transparent;
  box-shadow: var(--kb-shadow);
  border: 1px solid var(--kb-line);
}

.tools-kb-library {
  height: 100%;
}

/* 覆盖内嵌知识库样式，保持功能不变，仅调整观感 */
/deep/ .knowledge-page {
  height: 100%;
  background: transparent;
}

/deep/ .knowledge-main-row {
  gap: 0;
  background: #f7fafb;
}

/deep/ .knowledge-left-panel {
  width: 300px !important;
  background: linear-gradient(180deg, #ffffff 0%, #f7fbfa 100%);
  border-right: 1px solid var(--kb-line);
}

/deep/ .directory-panel-search {
  padding: 12px 12px 10px;
  background: transparent;
  border-bottom: 1px solid var(--kb-line);
}

/deep/ .directory-search-input .ivu-input {
  border-radius: 10px;
  border-color: transparent;
  background: #eef6f5;
  transition: all 0.2s ease;

  &:hover,
  &:focus {
    border-color: rgba(26, 166, 160, 0.35);
    background: #fff;
    box-shadow: 0 0 0 3px rgba(26, 166, 160, 0.12);
  }
}

/deep/ .directory-view-filter-btn {
  border-radius: 999px;
  border-color: transparent;
  background: #eef6f5;
  color: var(--kb-muted);

  &:hover {
    color: var(--kb-accent-strong);
    border-color: transparent;
    background: #dff3f0;
  }

  &.active {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, var(--kb-accent), #3ec6a8);
    box-shadow: 0 4px 10px rgba(26, 166, 160, 0.25);
  }
}

/deep/ .directory-panel-header {
  padding: 10px 12px;
  min-height: 44px;
  border-bottom: 1px solid var(--kb-line);
  background: rgba(255, 255, 255, 0.7);
}

/deep/ .directory-panel-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--kb-ink);
  letter-spacing: 0.3px;
}

/deep/ .directory-config-btn {
  width: 30px;
  height: 30px;
  border-radius: 10px;
  border-color: transparent;
  background: #eef6f5;
  color: var(--kb-muted);

  &:hover {
    color: var(--kb-accent-strong);
    border-color: transparent;
    background: #dff3f0;
    transform: translateY(-1px);
  }
}

/deep/ .data-panel-header {
  padding: 10px 14px;
  gap: 14px;
  background: rgba(255, 255, 255, 0.92);
  border-bottom: 1px solid var(--kb-line);
}

/deep/ .data-search-input .ivu-input {
  border-radius: 10px;
  border-color: transparent;
  background: #eef6f5;

  &:hover,
  &:focus {
    border-color: rgba(26, 166, 160, 0.35);
    background: #fff;
    box-shadow: 0 0 0 3px rgba(26, 166, 160, 0.12);
  }
}

/deep/ .knowledge-list {
  background:
    radial-gradient(circle at 90% 10%, rgba(255, 138, 91, 0.05), transparent 28%),
    linear-gradient(180deg, #f9fcfc 0%, #f3f7f8 100%) !important;
}

/deep/ .knowledge-status-bar {
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.95);
  border-top: 1px solid var(--kb-line);
  color: var(--kb-muted);
}

/deep/ .status-path-link {
  color: var(--kb-ink);
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: var(--kb-accent-strong);
  }
}

/deep/ .status-count,
/deep/ .status-role-value {
  color: var(--kb-accent-strong);
  font-weight: 600;
}

/deep/ .l-empty-message {
  color: var(--kb-muted) !important;
  font-weight: 500;
}

/deep/ .kng-empty-upload-btn {
  margin-top: 10px;
  border-radius: 999px !important;
  background: linear-gradient(135deg, var(--kb-accent), #3ec6a8) !important;
  border: none !important;
  color: #fff !important;
  box-shadow: 0 8px 16px rgba(26, 166, 160, 0.25);
}

@media (max-width: 768px) {
  .tools-kb-shell {
    padding: 8px;
  }

  /deep/ .knowledge-main-row {
    flex-direction: column;
  }

  /deep/ .knowledge-left-panel {
    width: 100% !important;
    max-height: 38%;
    border-right: none;
    border-bottom: 1px solid var(--kb-line);
  }
}
</style>
